import { Parser } from 'html-to-react'
import { observer } from 'mobx-react'
import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { AnnouncementStoreContext } from '@/stores/AnnouncementStore'

const isInternalLink = (url) => typeof url === 'string' && url.startsWith('/')

/**
 * AnnouncementDetailModal
 *
 * Global, store-driven modal that shows an announcement's long-form `body`
 * (e.g. full release notes). Opened via AnnouncementStore.openDetail(id) from
 * either the banner or the notification bell, so only one instance is mounted
 * (in Routes) rather than one per surface.
 */
const AnnouncementDetailModal = observer(() => {
  const store = useContext(AnnouncementStoreContext)
  const navigate = useNavigate()

  const announcement = store.activeDetail
  if (!announcement) return null

  const handleClose = () => store.closeDetail()

  const handleCtaClick = () => {
    if (!announcement.linkUrl) return
    if (isInternalLink(announcement.linkUrl)) {
      store.closeDetail()
      navigate(announcement.linkUrl)
    } else {
      window.open(announcement.linkUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Modal
      open
      width="large"
      title={announcement.title}
      onClose={handleClose}
      setOpen={handleClose}
      fullWidthButtons={false}
      content={
        <div className="my-6 px-2">
          {/*
            body is authored via the admin's rich-text field and sanitized
            server-side with bleach (RichTextField) before it is ever stored.
            html-to-react's Parser builds React elements (no
            dangerouslySetInnerHTML), so no inline scripts/handlers execute here.
          */}
          <div className="prose prose-sm max-w-none text-gray-700">
            {Parser().parse(announcement.body || '')}
          </div>

          {announcement.linkUrl && (
            <div className="mt-6 flex justify-start">
              <Button
                label={announcement.linkLabel || 'Learn more'}
                onClick={handleCtaClick}
                size="sm"
              />
            </div>
          )}
        </div>
      }
      actions={[{ type: 'cancel', label: 'Got it', onClick: handleClose }]}
    />
  )
})

export default AnnouncementDetailModal
